import { formatMoney } from "@bw/shared/utils";
import type {
  PricingRuleResponse,
  PublicProgramSessionResponse,
  ProgramTrackResponse,
  UnifiedProgramSessionResponse,
} from "@bw/shared/types/generated";

/**
 * Pure display helpers for the classes journey. Every value here is rendered
 * verbatim from a backend field — NO pricing, capacity, or eligibility math.
 * `available_spots` decides the capacity label; we never derive "sold out" from
 * enrolled/max ourselves.
 */

/** Human label for a backend program_type / track skill_level / fee_type. */
export function titleCase(value: string | null | undefined): string {
  if (!value) return "";
  return value.charAt(0).toUpperCase() + value.slice(1);
}

/**
 * Resolve a track's BASE price rule from the facility's pricing rules. Slice
 * 6.1 (offering-keyed class pricing): a track's price is NOT a field on
 * ProgramTrackResponse — it IS a `service="classes"`, `offering_id=track_id`
 * pricing rule. An offering can carry MORE than one such rule — the
 * auto-minted base rule (`membership_scope==="any"`) PLUS any owner-created
 * member-discount rule(s) — so this deliberately matches `membership_scope
 * === "any"` (a facility has at most one, enforced by a partial-unique index)
 * rather than the first array match, mirroring the backend's own
 * `get_pricing_rule_by_offering` base-rule targeting. A browsing player who
 * hasn't picked a payment method yet sees the standard (non-member) price;
 * the actual member discount, if any, is applied server-side at enroll time
 * via `get_applicable_price`. Returns undefined for an unpriced track (never
 * fabricated) — this can happen while the rules are still loading, or
 * (rarely) if the track's base rule doesn't resolve.
 */
export function findTrackPriceRule(
  rules: PricingRuleResponse[] | undefined,
  trackId: string,
): PricingRuleResponse | undefined {
  return (rules ?? []).find(
    (r) =>
      r.service === "classes" &&
      r.offering_id === trackId &&
      r.membership_scope === "any",
  );
}

/**
 * The "from" price across a program's tracks — the cheapest track's resolved
 * pricing-rule price, formatted via the money util. We pick the minimum by
 * comparing parsed numbers but ALWAYS render the original backend string
 * through formatMoney (never arithmetic on a Decimal). Returns "—" when there
 * are no tracks, or none of them have a resolved price rule yet.
 */
export function fromPriceLabel(
  tracks: ProgramTrackResponse[],
  rules: PricingRuleResponse[] | undefined,
): string {
  let cheapest: PricingRuleResponse | undefined;
  let cheapestN = Number.POSITIVE_INFINITY;
  for (const t of tracks) {
    const rule = findTrackPriceRule(rules, t.track_id);
    if (!rule) continue;
    const n = Number(rule.base_price);
    if (Number.isFinite(n) && n < cheapestN) {
      cheapest = rule;
      cheapestN = n;
    }
  }
  if (!cheapest) return "—";
  return formatMoney(cheapest.base_price, cheapest.currency);
}

/**
 * Spots-left label straight from the backend's available_spots — rendered
 * verbatim, never a client-computed "sold out". 0 stays an honest "0 spots
 * left"; the richer waitlist affordance lives on the track row / enroll page
 * (where the backend owns the enrolled-vs-waitlisted decision).
 */
export function spotsLabel(availableSpots: number): string {
  const n = Math.max(0, availableSpots);
  return `${n} ${n === 1 ? "spot" : "spots"} left`;
}

/** The age band straight from age_min/age_max, or null when the track is open. */
export function ageBandLabel(
  ageMin: number | null | undefined,
  ageMax: number | null | undefined,
): string | null {
  if (ageMin != null && ageMax != null) return `Ages ${ageMin}–${ageMax}`;
  if (ageMin != null) return `Ages ${ageMin}+`;
  if (ageMax != null) return `Ages up to ${ageMax}`;
  return null;
}

/** Whether a track is age-gated (the enroll form must collect student_age). */
export function isAgeGated(track: ProgramTrackResponse): boolean {
  return track.age_min != null || track.age_max != null;
}

/**
 * Format a session's start_time as a short date+time IN THE SESSION'S OWN
 * timezone (the backend returns an IANA tz alongside each occurrence). Rendering
 * in the venue's timezone — not the browser's — keeps "Tuesday 6pm" correct for
 * a player browsing from another tz, and avoids an off-by-one-day at the day
 * boundary. Falls back to the raw ISO string if either value is unusable.
 */
export function sessionDateLabel(
  session: UnifiedProgramSessionResponse | PublicProgramSessionResponse,
): string {
  const ms = Date.parse(session.start_time);
  if (!Number.isFinite(ms)) return session.start_time;
  try {
    return new Date(ms).toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      timeZone: session.timezone || "UTC",
    });
  } catch {
    return new Date(ms).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    });
  }
}

function clockParts(ms: number, timeZone: string): { clock: string; period: string } {
  const parts = new Intl.DateTimeFormat("en-US", {
    hour: "numeric",
    minute: "2-digit",
    timeZone,
  }).formatToParts(new Date(ms));
  const clock = parts
    .filter((part) => part.type === "hour" || part.type === "minute" || (part.type === "literal" && part.value === ":"))
    .map((part) => part.value)
    .join("");
  const period = parts.find((part) => part.type === "dayPeriod")?.value ?? "";
  return { clock, period };
}

/**
 * "4:58–6:28 PM" — a session's time from its start and scheduled length, in
 * the venue's timezone. Null when the start is unusable (never guessed).
 */
export function timeRangeLabel(
  startIso: string,
  durationMinutes: number | null | undefined,
  timeZone: string | null | undefined,
): string | null {
  const start = Date.parse(startIso);
  if (!Number.isFinite(start)) return null;
  const zone = timeZone || "UTC";
  try {
    const from = clockParts(start, zone);
    if (!durationMinutes || durationMinutes <= 0) return `${from.clock} ${from.period}`.trim();
    const to = clockParts(start + durationMinutes * 60_000, zone);
    const fromLabel = from.period === to.period ? from.clock : `${from.clock} ${from.period}`;
    return `${fromLabel}–${to.clock} ${to.period}`.trim();
  } catch {
    return null;
  }
}

type SessionLike = { start_time: string; end_time: string; timezone: string };

/** "Sat, Jan 19" for a session, in its own timezone (raw ISO if unusable). */
export function sessionDayLabel(session: SessionLike): string {
  const ms = Date.parse(session.start_time);
  if (!Number.isFinite(ms)) return session.start_time;
  const options = { weekday: "short", month: "short", day: "numeric" } as const;
  try {
    return new Date(ms).toLocaleDateString("en-US", { ...options, timeZone: session.timezone || "UTC" });
  } catch {
    return new Date(ms).toLocaleDateString("en-US", { ...options, timeZone: "UTC" });
  }
}

/** "4:58–6:28 PM" from a session's real start and end. */
export function sessionTimeLabel(session: SessionLike): string | null {
  const start = Date.parse(session.start_time);
  const end = Date.parse(session.end_time);
  if (!Number.isFinite(start)) return null;
  const minutes = Number.isFinite(end) && end > start ? Math.round((end - start) / 60_000) : null;
  return timeRangeLabel(session.start_time, minutes, session.timezone);
}

/** The schedule fact for a class row: its session time, from real fields only. */
export function programScheduleLabel(program: {
  start_date: string;
  schedule_duration_minutes: number;
  timezone: string;
}): string | null {
  return timeRangeLabel(program.start_date, program.schedule_duration_minutes, program.timezone);
}
